'use client';

/**
 * Web analytics adapter for GTM / GA4.
 *
 * Pushes events to window.dataLayer for Google Tag Manager.
 * GTM forwards these to GA4 (and any other configured tags).
 * No-ops during SSR. All event params are sanitized to strip PII.
 */

import { sanitizeParams, type EventParams } from '@planit.family/analytics';
import type { AnalyticsUserProperties } from '@planit.family/analytics';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

function pushToDataLayer(data: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * Track a custom event via GTM dataLayer.
 *
 * @param eventName - Event name following area.action_verb convention
 * @param params - Optional event parameters (PII will be stripped)
 */
export function trackEvent(eventName: string, params?: EventParams): void {
  const sanitized = params ? sanitizeParams(params) : {};
  pushToDataLayer({ event: eventName, ...sanitized });
}

/**
 * Set the user ID in the dataLayer. Pass null to clear on logout.
 */
export function setUserId(userId: string | null): void {
  pushToDataLayer({ event: 'set_user_id', user_id: userId });
}

/**
 * Set user properties (custom dimensions) in the dataLayer.
 */
export function setUserProperties(properties: Partial<AnalyticsUserProperties>): void {
  pushToDataLayer({ event: 'set_user_properties', user_properties: properties });
}
